import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Master v5 Prompt 8 — shared autosave contract for a single mutable
 * entity (PresentationContext, IdeaCard, Section, StructureBeat,
 * DeliveryAnnotation).
 *
 * One shared debounce/idempotency-key/revision cycle covers the WHOLE
 * entity, not one per field — these entities have multiple free-text
 * fields sharing a single `revision`, so editing two fields within the
 * same debounce window must never race each other into a false conflict.
 *
 * - debounced: `setField` resets a single ~800ms timer; only the final
 *   state after the user pauses is ever sent.
 * - idempotent retry: the same `idempotency_key` is reused for every
 *   retry of one pending edit; a fresh key is only minted once the user
 *   changes the content again.
 * - optimistic revision: `expected_revision` always reflects the last
 *   server-confirmed revision this hook has seen.
 * - stale conflict never silently overwrites: a 409 surfaces the
 *   server's current document; the caller explicitly chooses to keep
 *   their edit (retried with the fresh revision) or take the server's.
 * - server-driven prop updates (e.g. a query refetch after some OTHER
 *   action) are only adopted while status is "saved" — an in-flight or
 *   unresolved-conflict local edit is never clobbered by them.
 */
export function useAutosaveEntity({ initialValues, revision, onSave, debounceMs = 800 }) {
  const [values, setValues] = useState(initialValues);
  const [status, setStatus] = useState("saved"); // saved | pending | saving | error | conflict
  const [conflict, setConflict] = useState(null);

  const statusRef = useRef("saved");
  const revisionRef = useRef(revision);
  const valuesRef = useRef(initialValues);
  const idKeyRef = useRef(null);
  const timerRef = useRef(null);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const setStatusBoth = useCallback((next) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  useEffect(() => {
    if (statusRef.current !== "saved") return; // never clobber a pending edit or an unresolved conflict
    setValues(initialValues);
    valuesRef.current = initialValues;
    revisionRef.current = revision;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialValues), revision]);

  const flush = useCallback(async () => {
    if (idKeyRef.current == null) {
      idKeyRef.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    setStatusBoth("saving");
    try {
      const result = await onSaveRef.current({
        ...valuesRef.current,
        expected_revision: revisionRef.current,
        idempotency_key: idKeyRef.current,
      });
      revisionRef.current = result.revision;
      idKeyRef.current = null;
      setStatusBoth("saved");
    } catch (err) {
      if (err?.status === 409) {
        setConflict(err.body?.detail?.current || null);
        setStatusBoth("conflict");
      } else {
        setStatusBoth("error");
      }
    }
  }, [setStatusBoth]);

  const setField = useCallback((field, fieldValue) => {
    const next = { ...valuesRef.current, [field]: fieldValue };
    valuesRef.current = next;
    setValues(next);
    idKeyRef.current = null; // new content -> a fresh save attempt
    setStatusBoth("pending");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, debounceMs);
  }, [flush, debounceMs, setStatusBoth]);

  const retry = useCallback(() => flush(), [flush]);

  const keepMineAfterConflict = useCallback(() => {
    if (!conflict) return;
    revisionRef.current = conflict.revision;
    setConflict(null);
    flush();
  }, [conflict, flush]);

  const useServerAfterConflict = useCallback(() => {
    if (!conflict) return;
    revisionRef.current = conflict.revision;
    valuesRef.current = conflict;
    setValues(conflict);
    setConflict(null);
    setStatusBoth("saved");
  }, [conflict, setStatusBoth]);

  return { values, status, conflict, setField, retry, keepMineAfterConflict, useServerAfterConflict };
}
